import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';
import DurationDisplay, { formatFileSize } from '../common/DurationDisplay';
import ConfirmDialog from '../common/ConfirmDialog';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Spots' },
  { value: 'ready', label: 'Ready' },
  { value: 'processing', label: 'Processing' },
  { value: 'error', label: 'Error' },
  { value: 'draft', label: 'Draft' },
];

export default function SpotList() {
  const [spots, setSpots] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [search, setSearch] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);

  useEffect(() => {
    fetchCampaigns();
    fetchSpots();
  }, [statusFilter, campaignFilter, search]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get('/api/campaigns');
      setCampaigns(data);
    } catch (err) {
      console.error('[SpotList] Failed to fetch campaigns:', err);
    }
  };

  const fetchSpots = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (campaignFilter) params.campaign_id = campaignFilter;
      if (search) params.search = search;
      const { data } = await api.get('/api/advertising/spots', { params });
      setSpots(data);
    } catch (err) {
      console.error('[SpotList] Failed to fetch spots:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmItem) return;
    try {
      await api.delete(`/api/advertising/spots/${confirmItem.id}`);
      setSpots(spots.filter((s) => s.id !== confirmItem.id));
    } catch (err) {
      console.error('[SpotList] Delete failed:', err);
    } finally {
      setConfirmOpen(false);
      setConfirmItem(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Loading spots...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Spots</h1>
        <Link to="/advertising/spots/upload" className="btn btn-primary btn-sm">
          + Upload Spot
        </Link>
      </div>

      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Search spots..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input max-w-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input w-40 text-sm"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <select
          value={campaignFilter}
          onChange={(e) => setCampaignFilter(e.target.value)}
          className="input w-48 text-sm"
        >
          <option value="">All Campaigns</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {spots.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No spots found. Upload your first spot to get started.
        </div>
      ) : (
        <div className="space-y-3">
          {spots.map((spot) => (
            <div key={spot.id} className={`rounded-xl bg-dark-850 border p-4 ${spot.has_audio === false ? 'border-error-600/60' : 'border-dark-700'}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📢</span>
                  <div>
                    <h3 className="font-semibold text-dark-100">{spot.title || spot.name}
                      {spot.has_audio === false && (
                        <span className="ml-2 text-xs font-medium text-error-400">⚠ Sin audio (archivo borrado)</span>
                      )}
                    </h3>
                    <p className="text-sm text-dark-400">
                      {spot.campaign_name && `Campaign: ${spot.campaign_name}`}
                      {spot.duration_seconds && (
                        <span className="mx-2">•</span>
                      )}
                      {spot.duration_seconds && <DurationDisplay seconds={spot.duration_seconds} />}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {spot.status && <StatusBadge status={spot.status} size="sm" />}
                  <button
                    onClick={() => {
                      setConfirmItem(spot);
                      setConfirmOpen(true);
                    }}
                    className="text-sm text-error-400 hover:text-error-300"
                    title="Delete"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {spot.play_count !== undefined && (
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-dark-400">
                    Plays: {spot.play_count} {spot.play_target ? `/ ${spot.play_target}` : ''}
                  </span>
                  {spot.file_size && (
                    <span className="text-xs text-dark-500">{formatFileSize(spot.file_size)}</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Delete Spot"
        message={`Are you sure you want to delete "${confirmItem?.title}"?`}
        danger
        onConfirm={handleDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
